import { mapActions, mapState, mapMutations } from 'vuex'
import SearchPageContent from '../search/SearchPageContent.vue'
import SearchLayoutInput from '../search/SearchLayoutInput.vue'
import SearchResultLayout from '../search/SearchResultLayout.vue'
import SearchLoaderContainer from '../common/search/SearchLoaderContainer.vue'
import SearchResultsContainer from '../search/SearchResultsContainer.vue'
import {
  stateOptions,
  SEARCH_LIMIT,
  rentPrices,
  salePrices,
  INITIAL_NEW_QUERY
} from '../common/searchPropertyConst'
import { splitSemiColonString } from '../../utils/propertyUtils'
import { isEmpty } from '../../utils/generalUtils'

export default {
  components: {
    SearchPageContent,
    SearchLayoutInput,
    SearchResultLayout,
    SearchLoaderContainer,
    SearchResultsContainer
  },

  data() {
    return {
      searchTypeOptions: [
        { name: 'sale', value: 0, text: 'for sale' },
        { name: 'rent', value: 1, text: 'for rent' }
      ],
      scrolled: false,
      resultHeaderOptions: null,
      maxSalePrice: '10000000',
      maxRentPrice: '5000',
      resultContentElement: null,
      isResultsScrolled: false
    }
  },

  computed: {
    /**
     * Mapping the search result object from the store
     */
    ...mapState('common', [
      'lastNewQuery',
      'isMobile',
      'searchObject',
      'searchYOffset',
      'profileStatus',
      'addressInputValue',
      'multipleRequestedObject',
      'refreshSearch'
    ]),
    ...mapState('home', ['windowWidth']),

    isEmptySearchObject() {
      return isEmpty(this.searchObject.data)
    },

    getTopHeader() {
      if (isEmpty(this.searchObject.data)) {
        return this.searchObject.loading
          ? 'Loading Properties...'
          : `No properties matching ${this.getTopHeaderText}`
      } else {
        return `Properties: ${this.getTopHeaderText}`
      }
    },

    getTopHeaderText() {
      if (this.resultHeaderOptions) {
        // This part isn't being handled by google autocomplete box
        // Make sure that an alternate method is provided for it
        if (this.resultHeaderOptions.mls) {
          return `MLS# '${this.resultHeaderOptions.mls}'`
        }
        if (this.resultHeaderOptions.postal) {
          return `Zip Code: ${this.resultHeaderOptions.postal}`
        }
        if (this.resultHeaderOptions.state) {
          return this.resultHeaderOptions.city
            ? `'${this.resultHeaderOptions.city}' (${this.getStateName(
              this.resultHeaderOptions.state
            )})`
            : this.getStateName(this.resultHeaderOptions.state)
        }
        if (this.resultHeaderOptions.city) {
          return `'${this.resultHeaderOptions.city}'`
        }
      }
      return 'All Areas'
    },

    cardHeight() {
      return (
        document.querySelector('.results-layout-card').offsetHeight + 15 || null
      )
    },

    searchLayoutComps() {
      let saleComps = [
        { name: 'search-address-input' },
        { name: 'property-type-select' },
        { name: 'bed-bath-input' },
        {
          name: 'price-range-selector',
          props: {
            'price-options': salePrices(),
            'max-price': this.maxSalePrice
          }
        },
        { name: 'sale-filter-dropdown' }
      ]
      // if (
      //   this.searchObject.searchType === 'sale'
      //   // !(this.searchObject.searchResult && this.windowWidth < 1600)
      // ) {
      //   saleComps.splice(4, 0, { name: 'include-type-select' })
      // }

      // if (this.searchObject.searchResult && this.windowWidth < 1264) {
      //   saleComps = saleComps.filter(item => item.name !== 'property-type-select')
      // }

      // if (this.searchObject.searchResult && this.windowWidth < 960) {
      //   saleComps = saleComps.filter(item => item.name !== 'bed-bath-input')
      // }

      const rentComps = [
        { name: 'search-address-input' },
        { name: 'property-type-select' },
        {
          name: 'price-range-selector',
          props: {
            'price-options': rentPrices(),
            'max-price': this.maxRentPrice
          }
        },
        { name: 'bed-bath-input' },
        { name: 'pet-option-select' }
        // { name: 'rent-filter-dropdown' }
      ]

      return { sale: saleComps, rent: rentComps }
    }
  },

  /**
   * Retrieve the default properties list by sending get request
   * In this case all properties will be listed
   * We are retrieving 20 properties at a time
   * and more can be searched using infinite scroll
   */
  created() {
    if (isEmpty(this.searchObject.data) && !this.isMobile) {
      this.assignSearchElement()
    }

    // Commenting out this code - need discussion for max results to be displayed
    // if (this.searchObject.data.length > 100) {
    //   const searchArray = this.searchObject.data
    //   searchArray.length = 100
    //   this.setSearchObjectData(searchArray)
    // }
    // window.addEventListener('scroll', this.handleInfiniteScroll)
  },

  mounted() {
    this.resultContentElement = document.getElementById('resultContent')
    // this.resultContentElement.addEventListener('scroll', this.infiniteScroll)
    window.scrollTo(0, this.searchYOffset)
    if (this.profileStatus === 'agent') {
      this.searchTypeOptions.push({ name: 'sold', value: 2, text: 'sold' })
    }
  },

  beforeDestroy() {
    // window.removeEventListener('scroll', this.handleInfiniteScroll)
    if (this.resultContentElement) {
      this.resultContentElement.removeEventListener(
        'scroll',
        this.infiniteScroll
      )
    }
  },

  // updated() {
  //   if (this.refreshSearch) {
  //     this.assignSearchElement()
  //     this.refreshSearchPage(false)
  //   }
  // },

  methods: {
    ...mapActions('common', ['searchPropertiesList']),

    ...mapMutations('common', {
      setNewRequestedQuery: 'SET_NEW_REQUESTED_QUERY',
      setSearchLayoutType: 'SET_SEARCH_LAYOUT_TYPE',
      setSearchObjectData: 'SET_SEARCH_OBJECT_DATA',
      setSearchYOffset: 'SET_SEARCH_Y_OFFSET',
      setMarkedId: 'SET_MARKED_ID',
      setAddressInputValue: 'SET_ADDRESS_INPUT_VALUE',
      setSearchTextParams: 'SET_SEARCH_TEXT_PARAMS',
      resetMultipleRequestedObject: 'RESET_MULTIPLE_REQUESTED_OBJECT',
      resetSearchTextParams: 'RESET_SEARCH_TEXT_PARAMS',
      refreshSearchPage: 'REFRESH_SEARCH_PAGE',
      clearLastNewQuery: 'CLEAR_LAST_NEW_QUERY'
    }),

    getCityStateHeader() {
      if (this.resultHeaderOptions) {
        if (this.resultHeaderOptions.mls) {
          return `MLS# '${this.resultHeaderOptions.mls}'`
        }
        if (this.resultHeaderOptions.postal) {
          return `Zip Code: ${this.resultHeaderOptions.postal}`
        }
        if (this.resultHeaderOptions.state) {
          return this.resultHeaderOptions.city
            ? `'${this.resultHeaderOptions.city}' (${this.getStateName(
              this.resultHeaderOptions.state
            )})`
            : this.getStateName(this.resultHeaderOptions.state)
        }
        if (this.resultHeaderOptions.city) {
          return `'${this.resultHeaderOptions.city}'`
        }
      }
      return 'All Areas'
    },

    getStateName(stateCode) {
      return stateOptions.find(option => option.value === stateCode).text
    },

    infiniteScroll() {
      if (this.resultContentElement) {
        const scrollTop = this.resultContentElement.scrollTop
        let bottomScroll = null

        const removeCardFactor = this.searchObject.searchResult ? 3 : 2

        if (this.windowWidth >= 1904) {
          bottomScroll =
            (this.cardHeight *
              (this.searchObject.data.length - 2 * removeCardFactor)) /
            2
        } else {
          bottomScroll =
            this.cardHeight *
            (this.searchObject.data.length - removeCardFactor)
        }

        if (
          bottomScroll &&
          scrollTop > bottomScroll &&
          !this.isResultsScrolled
        ) {
          this.assignSearchElement()
          this.isResultsScrolled = true
          setTimeout(() => {
            this.isResultsScrolled = false
          }, 2000)
        }
      }
    },

    onMapMarkerClicked(value) {
      if (!isEmpty(value)) {
        const markerIndex = this.searchObject.data.findIndex(
          property => property._id === value._id
        )
        const topPosition =
          this.windowWidth < 1904
            ? markerIndex * this.cardHeight
            : parseInt(markerIndex / 2) * this.cardHeight
        this.resultContentElement.scrollTo({ top: topPosition - 50, left: 0 })
      }
    },

    /**
     * A scroll event listener that listens to window scrolling
     * and loads the search options in steps when reached at bottom
     */
    handleInfiniteScroll() {
      if (
        window.innerHeight + window.pageYOffset >=
        document.body.offsetHeight
      ) {
        // You're at the bottom of the page
        if (!this.scrolled) {
          this.assignSearchElement()
          this.scrolled = true
          setTimeout(() => {
            this.scrolled = false
          }, 3000)
        }
      }
    },

    toggleSearchType(item) {
      setTimeout(() => {
        this.setSearchLayoutType(item.name)
        let searchQuery = {
          ...INITIAL_NEW_QUERY,
          Status: item.name === 'sold' ? 'Sold' : 'Closed', // TODO: 'Active'
          PropertyType: item.name === 'rent' ? 'ResidentialLease' : 'Residential'
        }
        if (item.name === 'rent') {
          delete searchQuery.Foreclosure
          searchQuery = { ...searchQuery, Pets: false }
        } else {
          delete searchQuery.Pets
        }
        this.setAddressInputValue({ text: '', suggestion: false })
        this.resetMultipleRequestedObject()
        this.resetSearchTextParams()
        this.setNewRequestedQuery(searchQuery)
        if (!this.isMobile) {
          this.onSearchClicked()
        }
      }, 100)
    },

    startSearch() {
      const enteredText = this.addressInputValue.text
      // if (!this.addressInputValue.suggestion) {
      const enteredInput = splitSemiColonString(enteredText)
      // let placeObj = {
      //   City: [],
      //   PostalCode: [],
      //   StreetNumber: [],
      //   StreetName: [],
      //   // StreetSuffix: [],
      //   MLSNumber: [],
      //   Keywords: ''
      // }
      enteredInput.forEach(inputValue => {
        if (isNaN(inputValue)) {
          // Figure out a match pattern here
          // let keywordValue = this.lastNewQuery.query.Keywords
          // const query = addressInputPatternMatch(inputValue, { ...placeObj })
          const parts = inputValue.split(',')
          let query = {}
          if (parts.length === 1) {
            query.City = parts[0]
          }
          if (parts.length >= 3) {
            parts.pop() // country
            parts.pop() // state
            query.City = parts.pop()
          }
          this.setNewRequestedQuery({
            ...this.lastNewQuery.query,
            ...query
          })
        } else {
          if (inputValue.length === 5) { // Probably zip code
            let postalCodeArray = []
            if (!isEmpty(this.lastNewQuery.query.PostalCode)) {
              postalCodeArray = [...this.lastNewQuery.query.PostalCode]
            }
            if (!postalCodeArray.includes(inputValue)) postalCodeArray.push(inputValue)
            this.setNewRequestedQuery({
              ...this.lastNewQuery.query,
              PostalCode: postalCodeArray
            })
          } else { // Probably listing id (MLS id)
            let mlsArray = []
            if (!isEmpty(this.lastNewQuery.query.MLSNumber)) {
              mlsArray = [...this.lastNewQuery.query.MLSNumber]
            }
            if (!mlsArray.includes(inputValue)) mlsArray.push(inputValue)
            this.setNewRequestedQuery({
              ...this.lastNewQuery.query,
              MLSNumber: mlsArray
            })
          }
        }
      })
      // }
      this.setMarkedId('')
      this.resetSearchTextParams()
      this.setSearchTextParams({ ...this.multipleRequestedObject })
      this.resetMultipleRequestedObject()
      this.setSearchYOffset(0)
      this.setSearchObjectData([])
      this.setAddressInputValue({ text: '' })
      this.assignSearchElement()
    },

    assignSearchElement() {
      const { query } = this.lastNewQuery
      this.resultHeaderOptions = Object.assign(
        {},
        {
          city: query.City,
          state: query.StateOrProvince,
          type: query.PropertyType,
          mls: query.MLSNumber,
          postal: query.PostalCode
        }
      )
      const skipNumber = this.searchObject.data.length / SEARCH_LIMIT

      const searchElement = {
        index: {
          skip: SEARCH_LIMIT * skipNumber,
          limit: SEARCH_LIMIT
        },
        query
      }
      this.searchPropertiesList(searchElement)
    },

    showPropertyDetails(selectedProperty) {
      this.setSearchYOffset(window.pageYOffset)
      this.$router.push({
        path: `search/${selectedProperty._id}`
      })
    }
  }
}
